import { NumberField, SwitchField, TextField } from '~/common/components';
import { usePurchaseContext } from '~/purchase/context';
import { useI18n, Trans, useLocale } from '~/common/context';
import { Button, Flex, Icon, useConfirmation } from '@wyz/ui';
import { DEFAULT_EURO_SIGN } from '@wyz/utils';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useQuoteServicePricingPolicyForm } from '~/purchase/components';
import {
  getQuoteServicePriceTypeTranslation,
  QuoteServicePricingPolicyHelpers,
} from '~/purchase/helpers';
import { IPriceType, IQuoteServiceGeometryStepForm } from '~/purchase/types';

type Props = {
  priceType: IPriceType;
};

const CustomServiceTable: React.FC<Props> = ({ priceType }) => {
  const { t } = useI18n();
  const methods = useFormContext<IQuoteServiceGeometryStepForm>();
  const {
    fields: customServices,
    append,
    remove,
  } = useFieldArray<IQuoteServiceGeometryStepForm, 'customServices', 'key'>({
    control: methods.control,
    name: 'customServices',
    keyName: 'key',
  });
  const ask = useConfirmation();
  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  const { state } = usePurchaseContext();
  const locale = useLocale();
  const currencySymbol = state.targetCustomer?.deliveryInformation?.country
    .currencyIsoCode
    ? locale.formatters.currency(
        state.targetCustomer?.deliveryInformation?.country.currencyIsoCode,
      )
    : DEFAULT_EURO_SIGN;

  return (
    <div>
      <table className='service-refinement-table'>
        <thead>
          <tr>
            <th style={{ width: '10%' }}>
              <Trans i18nKey='app.common.service.quote.code' />
            </th>
            <th style={{ width: '70%' }}>
              <Trans i18nKey='app.common.description' />
            </th>
            <th style={{ width: '10%' }}>
              <Trans
                i18nKey={getQuoteServicePriceTypeTranslation(priceType, 'unit')}
              />
            </th>
            <th>
              <Trans i18nKey='app.common.status' />
            </th>
            <th>
              <Trans i18nKey='app.common.actions' />
            </th>
          </tr>
        </thead>
        <tbody>
          {customServices.map((customService, customServiceIdx) => {
            return (
              <tr key={customService.key}>
                <td align='center'>
                  <TextField
                    name={`customServices.${customServiceIdx}.code`}
                    placeholder={t('app.common.service.quote.code')}
                    formGroupClassName=''
                    feedbackType='tooltip'
                  />
                </td>
                <td align='center'>
                  <TextField
                    name={`customServices.${customServiceIdx}.description`}
                    placeholder={t('app.common.description')}
                    formGroupClassName=''
                    feedbackType='tooltip'
                  />
                </td>
                <td align='center'>
                  <NumberField
                    name={`customServices.${customServiceIdx}.price`}
                    placeholder='XX'
                    withKnobs={false}
                    feedbackType='tooltip'
                    sign={currencySymbol}
                  />
                </td>
                <td align='center'>
                  <SwitchField
                    name={`customServices.${customServiceIdx}.active`}
                    variant='SINGLE'
                    label=''
                    size='md'
                  />
                </td>
                <td align='center'>
                  {pricingPolicyForm.mode === 'WRITE' && (
                    <Flex alignItems='center' justifyContent='around'>
                      {customServiceIdx !== 0 && (
                        <Button
                          id={`remove-${customServiceIdx}`}
                          variant='link'
                          size='sm'
                          onClick={() => {
                            ask({
                              title: t(
                                'app.quote.pricing_policy.service.custom.table.remove_service',
                              ),
                              description: t(
                                'app.common.form.message_confirmation',
                              ),
                              cancelBtnText: t('app.common.button.cancel'),
                              confirmBtnText: t('app.common.buttons.remove'),
                              confirmBtnVariant: 'danger',
                            }).onConfirm(() => {
                              remove(customServiceIdx);
                            });
                          }}
                        >
                          <Icon icon='close' size='sm' />
                        </Button>
                      )}
                      <Button
                        id={`duplicate-${customServiceIdx}`}
                        variant='link'
                        size='sm'
                        onClick={() => {
                          append({
                            ...methods.getValues('customServices')[
                              customServiceIdx
                            ],
                            code: '',
                          });
                        }}
                      >
                        <Icon icon='duplicate' size='sm' />
                      </Button>
                    </Flex>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {pricingPolicyForm.mode === 'WRITE' && (
        <>
          <div className='space-y-2'>
            {QuoteServicePricingPolicyHelpers.CUSTOM_SERVICE_SUGGESTIONS.map(
              (suggestionKey) => {
                const translatedValue = t(suggestionKey);
                return (
                  <Button
                    key={suggestionKey}
                    variant='link'
                    id={`add-${translatedValue}`}
                    onClick={() => {
                      append({
                        description: translatedValue,
                        code: '',
                        price: undefined as unknown as number,
                        active: true,
                      });
                    }}
                  >
                    <Icon icon='plus' />
                    <span>{translatedValue}</span>
                  </Button>
                );
              },
            )}
          </div>
          <div className='mt-3'>
            <Button
              variant='primary'
              type='button'
              onClick={() =>
                append(
                  QuoteServicePricingPolicyHelpers.getCustomServiceDefaultValue(),
                )
              }
              id='add-service'
            >
              <Trans i18nKey='app.quote.pricing_policy.service.custom.table.add_service' />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomServiceTable;
